import { Fragment, ReactNode, useCallback, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Box } from '@mui/system';
import { Tab, Tabs } from '@mui/material';

import { useTabs } from '@shared/hooks/useTabs';
import { withQueryParams } from '../commonFunctions';
import { ShadowDivider } from '../styledComponents';
import { LoadingOverlay } from '../components/grid/overlays/LoadingOverlay';

const LoadingWrapper = styled.div`
  position: relative;
  max-height: 60vh;
  height: 540px;

  .MuiLinearProgress-root {
    margin: -30px -20px 0;
    width: calc(100% + 40px);
  }
`;

interface ILocalTab {
  name: string;
  component: ReactNode;
  disabled?: boolean;
}

interface ILocalTabsParams {
  data: ILocalTab[];
  onChangeTab?: (tabIndex: number) => void;
  loading?: boolean;
  hideShadow?: boolean;
  activeTab?: number;
  scrollable?: boolean;
}

export const NavTabs = ({
  data,
  loading = false,
  hideShadow = false,
  scrollable = true,
}: ILocalTabsParams) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { updateCurrentTab } = useTabs();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('tab');

  const handleChange = useCallback(
    (_event: React.SyntheticEvent | null, newValue: number) => {

      const routeToUpdate = withQueryParams(pathname, { tab: newValue });

      updateCurrentTab({
        route: routeToUpdate,
      });

      navigate(withQueryParams('.', { tab: newValue }), { replace: true });
    },
    [pathname]
  );

  useEffect(
    () => {
      if (currentTab === null) {
        setSearchParams(params => [...params, ['tab', '0']]);
      }
    },
    [currentTab]
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs
        variant={scrollable ? 'scrollable' : 'standard'}
        scrollButtons={scrollable ? 'auto' : false}
        value={Number(currentTab) || 0}
        onChange={handleChange}
      >
        {data.map((tab, index) => (
          <Tab
            key={index}
            label={tab.name}
            disabled={tab?.disabled ?? false}
          />
        ))}
      </Tabs>

      {!hideShadow && <ShadowDivider />}

      {loading ? (
        <LoadingWrapper>
          <LoadingOverlay />
        </LoadingWrapper>
      ) : (
        <Fragment>
          {data[Number(currentTab) || 0]?.component}
        </Fragment>
      )}
    </Box>
  );
};
